import React from "react";
import { Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StepCard } from "./_helpers";
import { totalComponentPercent } from "@/lib/grading";

/** Step 4 — Categories (Subjects / Learning Areas) with their Assessment Components. */
export default function Step4Subjects({
  categories, groups, categorySettings, gradingMode,
  categoryLabel, categoryPlural,
  expanded, setExpanded,
  onAddCategory, onUpdateCategory, onDeleteCategory,
  onAddComponent, onUpdateComponent, onDeleteComponent,
}) {
  return (
    <StepCard step="Step 4" title={`${categoryPlural} & Assessment Components`}
      subtitle={`${categoryPlural} are shared across all grading periods. Assessment Tasks are added per period in the Grades module.`}
      testId="step-4"
    >
      <Button size="sm" variant="outline" onClick={onAddCategory} className="rounded-xl mb-3" data-testid="gs-add-category">
        <Plus className="w-3.5 h-3.5 mr-1" /> Add {categoryLabel}
      </Button>
      {categories.length === 0
        ? <p className="text-sm text-brand-inkMuted py-6 text-center bg-brand-purpleLight/30 rounded-xl">No {categoryPlural.toLowerCase()} yet.</p>
        : (
          <div className="space-y-3">
            {categories.map((cat) => {
              const expandedNow = expanded[cat.id] ?? true;
              const compTotal = totalComponentPercent(cat.components);
              const compOk = gradingMode !== "calculated" || cat.components.length === 0 || compTotal === 100;
              return (
                <div key={cat.id} className="border border-brand-purple/15 rounded-2xl overflow-hidden bg-[#FAFBFF]" data-testid={`gs-category-${cat.id}`}>
                  <div className="flex items-center gap-2 p-3 bg-white border-b border-brand-purple/10">
                    <button onClick={() => setExpanded((e) => ({ ...e, [cat.id]: !expandedNow }))} className="text-brand-purple">
                      {expandedNow ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <Input value={cat.category_name} onChange={(e) => onUpdateCategory(cat.id, { category_name: e.target.value })}
                      placeholder={`${categoryLabel} name`} className="flex-1 rounded-xl h-10" />
                    {categorySettings.grouping_enabled && (
                      <select value={cat.group_id || ""} onChange={(e) => onUpdateCategory(cat.id, { group_id: e.target.value || null })}
                        className="gs-select h-10 max-w-[180px]">
                        <option value="">— No group —</option>
                        {groups.map((g) => <option key={g.id} value={g.id}>{g.group_name || "(unnamed)"}</option>)}
                      </select>
                    )}
                    {gradingMode === "calculated" && cat.components.length > 0 && (
                      <span className={`text-xs font-heading font-bold px-2 py-1 rounded-full ${compOk ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#FDECEA] text-[#C62828]"}`}>{compTotal}%</span>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => onDeleteCategory(cat.id)} className="text-[#C62828]"
                      data-testid={`gs-del-category-${cat.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  {expandedNow && (
                    <div className="p-3 space-y-2">
                      {cat.components.length === 0 && <p className="text-xs text-brand-inkMuted text-center py-2">No components yet.</p>}
                      {cat.components.map((co) => (
                        <div key={co.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`gs-component-${co.id}`}>
                          <Input value={co.component_name} onChange={(e) => onUpdateComponent(cat.id, co.id, { component_name: e.target.value })}
                            placeholder="Component name (e.g. Quizzes)" className="col-span-8 rounded-xl h-10" />
                          <Input type="number" value={co.component_percentage} onChange={(e) => onUpdateComponent(cat.id, co.id, { component_percentage: Number(e.target.value) })}
                            placeholder="%" className="col-span-3 rounded-xl h-10" />
                          <Button size="icon" variant="ghost" onClick={() => onDeleteComponent(cat.id, co.id)} className="col-span-1 text-[#C62828]"
                            data-testid={`gs-del-component-${co.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      ))}
                      <Button size="sm" variant="outline" onClick={() => onAddComponent(cat.id)} className="rounded-xl mt-1"
                        data-testid={`gs-add-component-${cat.id}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add Component
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }
    </StepCard>
  );
}
